import { create } from 'zustand';

// localStorage helpers (matching Sidebar.jsx pattern)
const SIDEBAR_MINIMIZED_KEY = 'sidebar_minimized';
const THEME_KEY = 'app_theme';

const loadSidebarMinimized = () => {
  try {
    const stored = localStorage.getItem(SIDEBAR_MINIMIZED_KEY);
    return stored ? JSON.parse(stored) : true;
  } catch {
    return true;
  }
};

const saveSidebarMinimized = (value) => {
  try {
    localStorage.setItem(SIDEBAR_MINIMIZED_KEY, JSON.stringify(value));
  } catch {
    // localStorage unavailable — no-op
  }
};

const loadTheme = () => {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // localStorage unavailable — no-op
  }
  return 'dark';
};

const saveTheme = (theme) => {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    // localStorage unavailable — no-op
  }
};

const ACCENT_COLOR_KEY = 'app_accent_color';
const DEFAULT_ACCENT = '#F97316';

const loadAccentColor = () => {
  try {
    const stored = localStorage.getItem(ACCENT_COLOR_KEY);
    if (stored && /^#[0-9A-Fa-f]{6}$/.test(stored)) return stored;
  } catch {
    // localStorage unavailable — no-op
  }
  return DEFAULT_ACCENT;
};

const saveAccentColor = (color) => {
  try {
    localStorage.setItem(ACCENT_COLOR_KEY, color);
  } catch {
    // localStorage unavailable — no-op
  }
};

const CUSTOM_BG_KEY = 'app_custom_bg';

const loadCustomBg = () => {
  try {
    const stored = localStorage.getItem(CUSTOM_BG_KEY);
    if (stored && /^#[0-9A-Fa-f]{6}$/.test(stored)) return stored;
  } catch {
    // localStorage unavailable — no-op
  }
  return null;
};

const saveCustomBg = (color) => {
  try {
    if (color) {
      localStorage.setItem(CUSTOM_BG_KEY, color);
    } else {
      localStorage.removeItem(CUSTOM_BG_KEY);
    }
  } catch {
    // localStorage unavailable — no-op
  }
};

const useStore = create((set, get) => ({
  // User state
  user: null,
  setUser: (user) => set({ user }),

  // Tenant state
  currentTenant: 'default',
  setCurrentTenant: (tenant) => set({ currentTenant: tenant }),

  // Notification state
  notifications: [],
  addNotification: (notification) => set((state) => ({
    notifications: [...state.notifications, { id: Date.now(), ...notification }]
  })),
  clearNotifications: () => set({ notifications: [] }),

  // Dashboard refresh
  lastRefresh: null,
  setLastRefresh: () => set({ lastRefresh: new Date() }),

  // Sidebar minimize state
  sidebarMinimized: loadSidebarMinimized(),
  setSidebarMinimized: () => {
    const next = !get().sidebarMinimized;
    saveSidebarMinimized(next);
    set({ sidebarMinimized: next });
  },

  // Organisation switcher popover (sidebar). Opened from the sidebar button
  // and from the legacy /org/members?tab=workspaces link.
  orgSwitcherOpen: false,
  setOrgSwitcherOpen: (open) => set({ orgSwitcherOpen: open }),

  // Theme state
  theme: loadTheme(),
  setTheme: (theme) => {
    saveTheme(theme);
    set({ theme });
  },
  toggleTheme: () => {
    const next = get().theme === 'dark' ? 'light' : 'dark';
    saveTheme(next);
    set({ theme: next });
  },

  // Accent color state
  accentColor: loadAccentColor(),
  setAccentColor: (color) => {
    saveAccentColor(color);
    set({ accentColor: color });
  },

  // Custom background color state (null = use default dark/light theme)
  customBg: loadCustomBg(),
  setCustomBg: (color) => {
    saveCustomBg(color);
    set({ customBg: color });
  },
}));

export default useStore;
